import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { PartInfo } from "../../../../shared/models";
import {
  setRoofOnline,
  setRoofOffline,
  installRoofPart,
  installExistingRoofPart,
  uninstallRoofPart,
  repairRoofPart,
  upsertRoofPartDetails,
  createSparePart,
  deleteSparePart,
} from "../../repositories/RoofRepository";
/** Invalidate all roof caches (tree + details). */
const invalidateAll = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ["roof-tree"] });
  qc.invalidateQueries({ queryKey: ["roof-details"] });
};

// =============================================================================
// Set Online / Set Offline
// =============================================================================

interface SetRoofOnlineParams {
  roofPartId: number;
  parentPartId: number;
  timestamp: Date | null;
}

interface SetRoofOfflineParams {
  roofPartId: number;
  timestamp: Date | null;
}

export const useSetRoofOnlineMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: SetRoofOnlineParams) => {
      const resp = await setRoofOnline(
        params.roofPartId,
        params.parentPartId,
        undefined,
        undefined,
        params.timestamp,
      );
      if (!resp.ok)
        throw new Error(resp.message ?? "Failed to set roof online");
      return resp;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
};

export const useSetRoofOfflineMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: SetRoofOfflineParams) => {
      const resp = await setRoofOffline(
        params.roofPartId,
        undefined,
        undefined,
        params.timestamp,
      );
      if (!resp.ok)
        throw new Error(resp.message ?? "Failed to set roof offline");
      return resp;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
};

// =============================================================================
// Install Part (new + existing)
// =============================================================================

interface InstallRoofPartParams {
  roofPartId: number;
  positionId: number;
  partTypeId: number;
  partInfo: PartInfo;
  comments: string | null;
  operatorNumber?: number;
  timestamp: Date | null;
}

export const useInstallRoofPartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: InstallRoofPartParams) => {
      const resp = await installRoofPart(
        params.roofPartId,
        params.positionId,
        params.partTypeId,
        params.partInfo,
        params.comments,
        params.operatorNumber,
        undefined,
        params.timestamp,
      );
      if (!resp.ok) throw new Error(resp.message ?? "Failed to install part");
      return resp;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
};

interface InstallExistingPartParams {
  existingPartId: number;
  roofPartId: number;
  positionId: number;
  comments: string | null;
  operatorNumber?: number;
  timestamp: Date | null;
}

export const useInstallExistingPartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: InstallExistingPartParams) => {
      const resp = await installExistingRoofPart(
        params.existingPartId,
        params.roofPartId,
        params.positionId,
        params.comments,
        params.operatorNumber,
        undefined,
        params.timestamp,
      );
      if (!resp.ok) throw new Error(resp.message ?? "Failed to install part");
      return resp;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
};

// =============================================================================
// Uninstall / Repair
// =============================================================================

interface UninstallPartParams {
  partId: number;
  comments: string | null;
  operatorNumber?: number;
  timestamp: Date | null;
}

export const useUninstallRoofPartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: UninstallPartParams) => {
      const resp = await uninstallRoofPart(
        params.partId,
        params.operatorNumber,
        params.comments,
        params.timestamp,
      );
      if (!resp.ok) throw new Error(resp.message ?? "Failed to uninstall part");
      return resp;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
};

interface RepairPartParams {
  partId: number;
  comments: string | null;
  operatorNumber?: number;
  timestamp: Date | null;
}

export const useRepairRoofPartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: RepairPartParams) => {
      const resp = await repairRoofPart(
        params.partId,
        params.operatorNumber,
        params.comments,
        params.timestamp,
      );
      if (!resp.ok) throw new Error(resp.message ?? "Failed to reset part");
      return resp;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
};

// =============================================================================
// Edit Part Details
// =============================================================================

interface UpsertPartDetailsParams {
  partId: number;
  partInfo: PartInfo;
  comments: string | null;
}

export const useUpsertRoofPartDetailsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: UpsertPartDetailsParams) => {
      const resp = await upsertRoofPartDetails(
        params.partId,
        params.partInfo,
        params.comments,
      );
      if (!resp.ok)
        throw new Error(resp.message ?? "Failed to update part details");
      return resp;
    },
    onSuccess: () => {
      // Details only — no tree structure change
      queryClient.invalidateQueries({ queryKey: ["roof-details"] });
    },
  });
};

// =============================================================================
// Spare Parts (create / delete)
// =============================================================================

interface CreateSparePartParams {
  containerId: number;
  partInfo: PartInfo;
  userId: number | null;
}

export const useCreateSparePartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: CreateSparePartParams) => {
      const resp = await createSparePart(
        params.containerId,
        params.partInfo,
        params.userId,
      );
      if (!resp.ok)
        throw new Error(resp.message ?? "Failed to create spare part");
      return resp;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
};

interface DeleteSparePartParams {
  partId: number;
  userId: number | null;
}

export const useDeleteSparePartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: DeleteSparePartParams) => {
      const resp = await deleteSparePart(params.partId, params.userId);
      if (!resp.ok)
        throw new Error(resp.message ?? "Failed to delete spare part");
      return resp;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
};
